import { Component, effect, Injector, OnDestroy, OnInit, resolveForwardRef, Type } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { SetLoadingStatus } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import {
  FetchTotalWealth,
  UpdateTotalWealthFlag,
} from '@app/finxone-web-frontend/app/lib/actions/total-wealth.action';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { WidgetsHideConditionsSelector } from '@app/finxone-web-frontend/app/lib/services/hide-conditions-service/hide-conditions-repository';
import { HideConditionsService } from '@app/finxone-web-frontend/app/lib/services/hide-conditions-service/hide-conditions.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  TotalWealthState,
  TotalWealthStateModel,
} from '@app/finxone-web-frontend/app/lib/state/total-wealth.state';
import { AccountListingStrategiesType } from '@app/finxone-web-frontend/app/lib/strategies/interfaces/account-listing-strategy.type';
import { WidgetsStrategyProvider } from '@app/finxone-web-frontend/app/lib/strategies/widgets-strategy.provider';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import {
  getPageSizeByProvider,
  getQueryParamValueByKey,
} from '@app/finxone-web-frontend/app/lib/utils/query-params.utils';
import { filterIfHandlebarNotRendered } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import {
  removeDuplicateCurrencies,
  removeInvalidCurrencies,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  accountSwitchDropdownTemplate,
  accountSwitchWidgetVisualTemplate,
} from '@finxone-platform/shared/default-templates';
import {
  AccountInfoType,
  AccountStatusType,
  AccountTypes,
  baseCssConfigStyle,
  BaseCssProperties,
  BaseWidgetProperties,
  DropdownMenuDirection,
  FormFieldConfig,
  InputLabelOptions,
  NewUiZoneWidgetConfig,
  PaymentProvider,
  ProviderClass,
  SavingsAccountInfoType,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { isArray } from 'lodash';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { SavingsAccountStateModel } from '../../../../lib/state/savings-account.state';
import { createChildWidgetInjector } from '../../../zones/utils';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { BaseComponent } from '../base/base.component';

export type DropdownListingInterface = Omit<
  AccountInfoType,
  | 'accountType'
  | 'organisationId'
  | 'customerId'
  | 'scheduledExchanges'
  | 'scheduledPayments'
  | 'customerName'
  | 'accountIdentifier'
> & {
  accountCategoryType: 'current' | 'multi-currency' | 'savings';
  accountType?: string;
  savingsAccountType?: string;
  currencyCode: string | { currency: string }[];
  totalBalance: string | { currency: string; amount: string }[];
  accountBalance: string | { currency: string; amount: string }[];
  sortCode: string;
  currencyFullName: string | { currency: string }[];
  accountNumber: string | undefined;
  bic: string | undefined;
  iban: string | undefined;
  providerExtraInfo: unknown;
  achAccountNumber: string;
  achNumber: string;
  abaAccountNumber: string;
  abaNumber: string;
  accountProviderExtraInfo: object;
  pendingExchangeNetTotal: number;
  pendingExchangeCount: number;
  pendingTransactionNetTotal: number;
  pendingTransactionCount: number;
  defaultMarker: string;
  countryCode: string;
  currencyName: string | { [x: string]: string } | undefined;
  accountStatus: string | undefined;
  accountName?: string | undefined;
  isFundingEnabled?: boolean | undefined;
  isWithdrawalEnabled?: boolean | undefined;
  isClosureEnabled?: boolean | undefined;
  maxDeposit?: string | undefined;
  interestRate?: number | undefined;
  annualEquivalantInterestRate?: number | null | undefined;
};

@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: 'finxone-web-frontend-accounts-context-dropdown-widget',
  templateUrl: './accounts-context-dropdown-widget.component.html',
  styleUrl: './accounts-context-dropdown-widget.component.scss',
})
export class AccountsContextDropdownWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  @Select(TotalWealthState.getTotalWealthState)
  public totalWealthState$!: Observable<TotalWealthStateModel>;

  public textContentStyle: baseCssConfigStyle;
  public currentActiveStyle: baseCssConfigStyle;
  public currentInactiveStyle: baseCssConfigStyle;
  public widgetConfig: BaseWidgetProperties;
  public currentInputStyle: baseCssConfigStyle;
  public accountsInfo: AccountStateModel | SavingsAccountStateModel;
  public accountsData: AccountInfoType[] | SavingsAccountInfoType[];
  public dropdownListing: DropdownListingInterface[] = [];
  public contextSwitch = true;
  public textContent: string;
  public activeInputCss: any = {};
  public inactiveInputCss: any = {};
  public inactiveBgColor: string;
  public activeBorderColor: string;
  public dropdownOpened = false;
  public isScrollable = true;
  public isScrollPeekEnabled = false;
  public visibleItemsCount = 5;
  public dropdownMenuDirection = DropdownMenuDirection.AUTO;
  public selectedAccount: DropdownListingInterface | null = null;
  currencies: Currency[];
  countries: Country[];
  pageNumber = 1;
  totalPages = 1;
  public widgetVisualTemplate = accountSwitchWidgetVisualTemplate;
  public dropdownTemplate = accountSwitchDropdownTemplate;
  public enableTotalWealth = false;
  public showFlag = true;
  public noRecordsTemplate = 'No data found';
  public renderedContent: string;
  public leftIconStyle: baseCssConfigStyle;
  public rightIconStyle: baseCssConfigStyle;
  public rightIcon: string;
  public leftIcon: string;
  public accountTypes = AccountTypes;

  public footerWidgetInstances: Array<{
    widgetName: string;
    component: Type<unknown>;
    injector: Injector;
  }> = [];

  private accountContextStatusList: AccountStatusType[];
  private totalWealthDefaultCurrencyOption: string;
  private totalWealthCustomCurrency: string;
  public showAllOpenEasyAccessAccounts: boolean;
  public openEasyAccessAccountsList: DropdownListingInterface[];
  public activeContextStrategy: AccountListingStrategiesType;
  private isLoading: boolean;

  private formId: string;
  public accountContextForm: FormGroup<{ [key: string]: FormControl }>;
  public labelType: InputLabelOptions;

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private store: Store,
    private hideConditionsService: HideConditionsService,
    private injector: Injector,
    private workflowFormsService: WorkflowFormsService,
    private zonePageUrlProvider: ZonePageUrlProvider,
  ) {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        //get the label type and text style class from root styles
        this.labelType = globalCssVariables['--inputFieldLabelType'] as InputLabelOptions;
      }
    });
    if (this.dataIn?.attri) {
      this.textContent = this.dataIn.attri['textContent'];
      this.widgetConfig = this.dataIn.attri.widgetProperties;

      this.contextSwitch = this.dataIn.attri?.['enableAccountContextSwitch'] ?? true;
      this.showAllOpenEasyAccessAccounts = this.dataIn.attri?.['showAllOpenEasyAccessAccounts'] ?? true;
      this.isScrollable = this.dataIn.attri.widgetProperties?.inputFieldConfig?.isScrollable ?? true;
      this.isScrollPeekEnabled =
        this.dataIn.attri.widgetProperties?.inputFieldConfig?.isScrollPeekEnabled ?? false;
      this.visibleItemsCount = this.dataIn.attri.widgetProperties?.inputFieldConfig?.visibleItemsCount ?? 5;
      this.dropdownMenuDirection =
        this.dataIn.attri.widgetProperties?.inputFieldConfig?.menuDirection ?? DropdownMenuDirection.AUTO;

      this.accountContextStatusList = this.dataIn.attri?.['accountContextList']?.map(
        (item: { id: AccountStatusType }) => item.id,
      ) ?? [AccountStatusType.active];
    }
    this.getWidgetCssConfig();

    this.configService
      .getCurrencies()
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          const validCurrencies = removeInvalidCurrencies(response);
          this.currencies = removeDuplicateCurrencies(validCurrencies, 'currency_code');
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.buildForm();
    // initialise the strategy provider
    const strategyProvider = new WidgetsStrategyProvider(this.injector);
    // get the strategy based on the widget context
    this.activeContextStrategy = strategyProvider.getAccountListingStrategy(this.context);
    this.activeContextStrategy
      .setupStateSubscription(this.store, this.pageNumber, this.pageSize, this.accountContextStatusList)
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          this.totalPages = response.accountTotalPages;

          // Saascada balances are provider-paginated (page size 5): client-side filtering
          // (fundable/withdrawable/closeable) is only correct once every page has been fetched,
          // otherwise matching accounts on later pages are silently missed. Other providers (e.g.
          // thisbank) already return the full account list in one call, so leave them untouched.
          if (
            this.pagingEnabled &&
            this.isSaascadaProvider &&
            this.pageNumber < this.totalPages &&
            this.activeContextStrategy.requiresFullAccountList(this.dataIn.attri)
          ) {
            this.isLoading = true;
            this.pageNumber = this.pageNumber + 1;
            this.activeContextStrategy.fetchNewAccounts(
              this.store,
              this.pageNumber,
              this.pageSize,
              this.accountContextStatusList,
            );
            return;
          }

          this.accountsInfo = response;
          this.accountsData = this.activeContextStrategy.getAccounts(response);
          this.dropdownListing = this.activeContextStrategy.getDropdownListing(
            response,
            this.store,
            this.currencies,
            this.dataIn.attri,
          );
          this.selectedAccount = this.activeContextStrategy.getSelectedAccountValueForDropdown(
            this.store,
            this.dataIn.attri,
          );
          this.isLoading = false;
          this.buildVisualTemplate();
        }),
      )
      .subscribe();
    this.configService.getCountries().subscribe({
      next: (data) => {
        this.countries = data;
      },
    });

    this.getWidgetConfig();
    this.buildVisualTemplate();

    this.loadNestedWidgetConfig();
  }

  private loadNestedWidgetConfig() {
    this.footerWidgetInstances = Object.entries(this.dataIn.widgetConfig?.nestedWidgets ?? {})
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, widgetConfig]) => !!widgetConfig?.widgetProperties?.['isNestedWidget']) // filter to only include widgets that are marked as nested widgets
      .map(([widgetName, widgetConfig]) => {
        const component = resolveForwardRef(getComponentByLabel(widgetName as WidgetNames));

        const widget = {
          name: widgetName,
          attributes: widgetConfig,
        } as NewUiZoneWidgetConfig;

        const injector = createChildWidgetInjector(widget, this.injector, this.dataIn, []);

        return {
          widgetName,
          component,
          injector,
        };
      });
  }

  public formInputId: string = this.dataIn?.attri?.['inputId'] || 'accountId';

  private buildForm() {
    this.formId = this.dataIn?.attri?.widgetProperties?.['parentForm'];
    if (!this.formId) return;

    const formConfig: FormFieldConfig[] = [
      {
        id: this.formInputId,
        type: 'select',
        label: 'Account Context Widget',
        parentForm: this.formId,
        name: this.formInputId,
        validation: {
          required: false,
        },
      },
    ];

    const formId = this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider);
    this.accountContextForm = this.workflowFormsService.getFormPart(formId) ?? new FormGroup({});
    this.workflowFormsService.addFormControl(this.accountContextForm, formConfig);
  }

  ngOnDestroy(): void {
    this.activeContextStrategy.clearData();
  }

  private getWidgetConfig() {
    this.enableTotalWealth = this.dataIn.attri?.['enableTotalWealth'] ?? false;
    this.totalWealthDefaultCurrencyOption =
      this.dataIn.attri?.['totalWealthDefaultCurrencyOption'] || 'account-context-currency';
    this.totalWealthCustomCurrency = this.dataIn.attri?.['totalWealthCustomCurrency'];
    this.showFlag = this.dataIn.attri?.['showFlag'] ?? true;
    this.noRecordsTemplate = this.dataIn.attri?.['noRecordsTemplate'] ?? 'No data found';
    this.dropdownTemplate = this.dataIn.attri?.['widgetDropdownTemplate'] ?? accountSwitchDropdownTemplate;
    this.leftIcon = this.dataIn?.attri?.widgetProperties?.lefticon ?? '';
    this.rightIcon = this.dataIn?.attri?.widgetProperties?.righticon ?? '';

    this.store.dispatch(new UpdateTotalWealthFlag(this.enableTotalWealth));
    this.getTotalWealthData();
  }

  private getTotalWealthData() {
    if (this.enableTotalWealth) {
      if (this.totalWealthDefaultCurrencyOption === 'custom-currency') {
        this.store.dispatch(new FetchTotalWealth(this.totalWealthCustomCurrency, this.enableTotalWealth));
      } else if (this.selectedAccount) {
        // this is just a stupid fallback for now to avoid errors
        // we need to refactor total wealth to support multi currency properly
        const currency = isArray(this.selectedAccount?.currency)
          ? this.selectedAccount?.currency[0]?.currency
          : this.selectedAccount?.currency;
        this.store.dispatch(new FetchTotalWealth(currency, this.enableTotalWealth));
      }
    }
  }

  public onAccountSelected(account: DropdownListingInterface) {
    this.selectedAccount = account;
    this.activeContextStrategy.selectAccount(account, this.store, this.dataIn.attri, this.enableTotalWealth);
    this.hideConditionsService.setHideConditionFromWidget(WidgetsHideConditionsSelector.SELECTED_ACCOUNT);
  }

  setInactiveStyle(isOpen: boolean) {
    this.currentInputStyle = isOpen ? this.currentActiveStyle : this.currentInactiveStyle;
  }

  public get pagingEnabled(): boolean {
    if (this.dataIn.attri['pagingEnabled'] != null) return this.dataIn.attri['pagingEnabled'];
    else return true;
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return this.dataIn.attri['pageSize'];
    return getPageSizeByProvider(this.store);
  }

  public get isSaascadaProvider(): boolean {
    const provider = this.store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
    return provider === PaymentProvider.SAASCADA;
  }

  private getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.BOX_SHADOW,
    ]);

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);

    const { baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.dataIn.attri?.widgetProperties,
    );

    /* * Sets the width of the active element to 'auto' if the component is a modal.
     * This ensures the modal's width is flexible and adjusts to its content,
     * overriding any fixed width styles that might be applied elsewhere.
     */
    if (this.isModal) {
      activeStyle.width = 'auto';
    }

    this.currentInactiveStyle = {
      ...inactiveStyle,
      ...activeStyle,
      ...this.textContentStyle,
      ...this.contentStyle,
    };

    this.currentActiveStyle = {
      ...activeStyle,
      ...baseStyle,
      ...this.textContentStyle,
      ...this.contentStyle,
    };

    this.currentInputStyle = this.currentInactiveStyle;

    this.leftIconStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.LEFT_ICON_SIZE,
      BaseCssProperties.LEFT_ICON_COLOR,
    ]);
    this.rightIconStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.RIGHT_ICON_SIZE,
      BaseCssProperties.RIGHT_ICON_COLOR,
    ]);
  }

  private get isModal() {
    return getQueryParamValueByKey('isModal');
  }

  getCountryNameByCountryCode(countryCode: string) {
    const currencyObject = this.countries?.find((v) => v.code === countryCode);
    return currencyObject?.['name'] ?? '';
  }

  public handleChildScroll() {
    if (this.pageNumber != this.totalPages && !this.isLoading) {
      this.store.dispatch(new SetLoadingStatus(true));
      this.pageNumber = this.pageNumber + 1;
      this.isLoading = true;
      this.activeContextStrategy.fetchNewAccounts(
        this.store,
        this.pageNumber,
        this.pageSize,
        this.accountContextStatusList,
      );
    }
  }

  private buildVisualTemplate() {
    this.widgetVisualTemplate =
      this.dataIn.attri?.['widgetVisualTemplate'] ?? accountSwitchWidgetVisualTemplate;
    if (this.widgetVisualTemplate) {
      const template = Handlebars.compile(this.widgetVisualTemplate);
      this.renderedContent = filterIfHandlebarNotRendered(template(this.selectedAccount));
    }
  }

  public get context():
    | AccountTypes.SAVINGS_ACCOUNT
    | AccountTypes.CURRENT_ACCOUNT
    | AccountTypes.OPEN_EASY_ACCESS_SAVINGS_ACCOUNT {
    if (this.dataIn.attri['context'] != null) return this.dataIn.attri['context'];
    else return AccountTypes.CURRENT_ACCOUNT;
  }
}
